import type { RangeOption } from './AppShell';
import type { ReadScope } from '@/api/hooks/useReadScope';
import { isStrictCalendarDate } from '@/lib/date';

export type Scope = {
  range: RangeOption | 'Custom';
  dateFrom?: string;
  dateTo?: string;
  dateScopeAvailability: 'ready' | 'invalid';
  rawDateFrom: string[];
  rawDateTo: string[];
  /** Canonical ordered account selection; an empty array is explicit All. */
  accountIds: readonly string[];
  /** Raw repeated account values, retained for honest invalid-scope recovery. */
  rawAccountFilters: readonly string[];
  accountScopeAvailability: 'ready' | 'invalid';
  /** @deprecated Compatibility accessor for scalar consumers; use accountIds. */
  accountFilter: string | null;
  readScope: ReadScope | null;
  needsDateReset: boolean;
};

const ACCOUNT_SCOPE_ALIASES = ['account_id', 'account_ids', 'accounts', 'account[]'];
const MAX_RAW_ACCOUNT_FILTERS = 100;
const MAX_ACCOUNT_IDS = 50;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RANGES: RangeOption[] = ['1M', '3M', 'YTD', '1Y', 'All'];

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function subtractMonths(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() - months;
  const day = date.getUTCDate();
  const target = new Date(Date.UTC(year, month + 1, 0));
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, target.getUTCDate())));
}

function readScopeValue(value: string | null): ReadScope | null {
  return value === 'current' || value === 'saved_history' ? value : null;
}

function normalizeAccountScope(params: URLSearchParams): Pick<Scope, 'accountIds' | 'rawAccountFilters' | 'accountScopeAvailability' | 'accountFilter'> {
  const rawAccountFilters = params.getAll('account');
  const hasAlias = ACCOUNT_SCOPE_ALIASES.some((key) => params.has(key));
  const normalized: string[] = [];
  let invalid = hasAlias || rawAccountFilters.length > MAX_RAW_ACCOUNT_FILTERS;

  for (const rawAccountFilter of rawAccountFilters) {
    if (!UUID_PATTERN.test(rawAccountFilter) || rawAccountFilter.includes(',')) {
      invalid = true;
      continue;
    }
    const accountId = rawAccountFilter.toLowerCase();
    if (!normalized.includes(accountId)) normalized.push(accountId);
  }

  if (normalized.length > MAX_ACCOUNT_IDS) invalid = true;
  const accountIds = Object.freeze(invalid ? [] : normalized.slice()) as readonly string[];
  return {
    accountIds,
    rawAccountFilters: Object.freeze(rawAccountFilters.slice()),
    accountScopeAvailability: invalid ? 'invalid' : 'ready',
    accountFilter: accountIds[0] ?? null,
  };
}

export function defaultOneMonth(now = new Date()): { from: string; to: string } {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = subtractMonths(to, 1);
  return { from: isoDate(from), to: isoDate(to) };
}

export function presetBounds(range: RangeOption, now: Date): { from?: string; to?: string } {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (range === 'All') return {};
  if (range === '1M') return defaultOneMonth(now);
  if (range === '3M') return { from: isoDate(subtractMonths(to, 3)), to: isoDate(to) };
  if (range === '1Y') return { from: isoDate(subtractMonths(to, 12)), to: isoDate(to) };
  return { from: `${to.getUTCFullYear()}-01-01`, to: isoDate(to) };
}

export function detectPreset(from: string, to: string, now = new Date()): RangeOption | 'Custom' {
  for (const range of RANGES) {
    const bounds = presetBounds(range, now);
    if (bounds.from === from && bounds.to === to) return range;
  }
  return 'Custom';
}

export function parseScope(search: string, now = new Date()): Scope {
  const params = new URLSearchParams(search);
  const accountScope = normalizeAccountScope(params);
  const rawDateFrom = params.getAll('from');
  const rawDateTo = params.getAll('to');
  const from = rawDateFrom[0] ?? null;
  const to = rawDateTo[0] ?? null;
  const readScope = readScopeValue(params.get('scope'));
  if (rawDateFrom.length === 0 && rawDateTo.length === 0) {
    return { range: 'All', dateScopeAvailability: 'ready', rawDateFrom, rawDateTo, ...accountScope, readScope, needsDateReset: false };
  }
  if (rawDateFrom.length !== 1 || rawDateTo.length !== 1 || !isStrictCalendarDate(from) || !isStrictCalendarDate(to) || from! > to!) {
    return { range: 'Custom', dateScopeAvailability: 'invalid', rawDateFrom, rawDateTo, ...accountScope, readScope, needsDateReset: false };
  }
  return { range: detectPreset(from, to, now), dateFrom: from, dateTo: to, dateScopeAvailability: 'ready', rawDateFrom, rawDateTo, ...accountScope, readScope, needsDateReset: false };
}

export type ScopeOverride = { account?: string | null; accountIds?: readonly string[]; readScope?: ReadScope | null };

type ReplaceableScope = Pick<Scope, 'range' | 'dateFrom' | 'dateTo'> & Partial<Pick<Scope, 'readScope' | 'dateScopeAvailability' | 'accountIds' | 'rawAccountFilters' | 'accountScopeAvailability' | 'accountFilter'>>;

function replacementAccountIds(scope: ReplaceableScope): readonly string[] | undefined {
  const hasAccountIds = Object.prototype.hasOwnProperty.call(scope, 'accountIds');
  const hasAccountFilter = Object.prototype.hasOwnProperty.call(scope, 'accountFilter');
  const accountIds = scope.accountIds ?? [];
  const accountFilter = scope.accountFilter ?? null;
  if (scope.accountScopeAvailability === 'invalid' && (scope.rawAccountFilters?.length ?? 0) > 0) return undefined;
  const scalarIsCompatibleAccessor = hasAccountIds && (accountIds.length === 0 ? accountFilter === null : accountIds[0] === accountFilter);
  if (hasAccountIds && (!hasAccountFilter || scalarIsCompatibleAccessor)) return accountIds;
  if (hasAccountFilter) return accountFilter ? [accountFilter] : [];
  return accountIds;
}

export function replaceScope(search: string, scope: ReplaceableScope, now = new Date()): string {
  let params = new URLSearchParams(search);
  params.delete('scope');
  if (Object.prototype.hasOwnProperty.call(scope, 'readScope') && scope.readScope) params.set('scope', scope.readScope);
  if (scope.range === 'All') { params.delete('from'); params.delete('to'); }
  else if (scope.dateFrom && scope.dateTo) { params.set('from', scope.dateFrom); params.set('to', scope.dateTo); }
  else if (scope.dateScopeAvailability === 'invalid') { /* preserve raw URL dates for non-date updates */ }
  else { const defaults = defaultOneMonth(now); params.set('from', defaults.from); params.set('to', defaults.to); }
  const accountIds = replacementAccountIds(scope);
  if (accountIds) {
    const entries = [...params.entries()];
    const hadAccount = entries.some(([key]) => key === 'account');
    params.delete('account');
    if (hadAccount && accountIds.length > 0) {
      const rebuilt = new URLSearchParams();
      let inserted = false;
      for (const [key, value] of entries) {
        if (key === 'account') {
          if (!inserted) {
            for (const accountId of accountIds) rebuilt.append('account', accountId);
            inserted = true;
          }
        } else rebuilt.append(key, value);
      }
      params = rebuilt;
    } else {
      for (const accountId of accountIds) params.append('account', accountId);
    }
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function scopeHref(target: string, sourceSearch: string, override: ScopeOverride = {}): string {
  const hashIndex = target.indexOf('#');
  const hash = hashIndex >= 0 ? target.slice(hashIndex) : '';
  const targetPath = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
  const queryIndex = targetPath.indexOf('?');
  const pathname = queryIndex >= 0 ? targetPath.slice(0, queryIndex) : targetPath;
  const destination = new URLSearchParams(queryIndex >= 0 ? targetPath.slice(queryIndex + 1) : '');
  const source = new URLSearchParams(sourceSearch);
  destination.delete('scope');
  for (const key of ['from', 'to', 'account']) {
    if ((key === 'from' || key === 'to') && !Object.prototype.hasOwnProperty.call(override, key)) {
      if (source.has(key)) {
        destination.delete(key);
        source.getAll(key).forEach((value) => destination.append(key, value));
      }
      continue;
    }
    if (key === 'account') {
      if (Object.prototype.hasOwnProperty.call(override, 'accountIds')) {
        destination.delete('account');
        for (const accountId of override.accountIds ?? []) destination.append('account', accountId);
      } else if (Object.prototype.hasOwnProperty.call(override, 'account')) {
        if (override.account) destination.set('account', override.account); else destination.delete('account');
      } else if (source.has('account')) {
        destination.delete('account');
        for (const accountId of source.getAll('account')) destination.append('account', accountId);
      }
      continue;
    }
    const value = Object.prototype.hasOwnProperty.call(override, key)
      ? override[key as keyof ScopeOverride]
      : source.get(key);
    if (value) destination.set(key, value as string);
    else if (key in override || source.has(key)) destination.delete(key);
  }
  if (Object.prototype.hasOwnProperty.call(override, 'readScope')) {
    if (override.readScope) destination.set('scope', override.readScope); else destination.delete('scope');
  }
  const query = destination.toString();
  return query ? `${pathname}?${query}${hash}` : `${pathname}${hash}`;
}
